/**
 * MobileNav — fixed bottom navigation, shown ONLY below `lg` (phones/tablets).
 *
 * The persistent sidebar takes over at lg+ (≥1024px), so this is hidden there
 * via `lg:hidden`. Below that there's no persistent sidebar, so this bottom tab
 * bar gives one-tap access to the core destinations (the top-bar hamburger still
 * opens the full sidebar drawer for the rest, e.g. Social Automator). Workspace
 * (chat) and Unified Inbox show an unread badge.
 */
import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Briefcase, Workflow, MessageCircle, Inbox as InboxIcon, User } from 'lucide-react';
import { useChatStore } from '../stores/chatStore';
import { useInboxStore } from '../stores/inboxStore';
import { hapticTap } from '../lib/nativeApp';
import { prefetchRoute } from '../app/routePrefetch';

const itemClass = (active: boolean): string =>
  `flex-1 min-h-[56px] flex flex-col items-center justify-center gap-0.5 text-[10px] font-medium transition-colors ` +
  `focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-gray-900 ` +
  (active ? 'text-gray-900' : 'text-gray-500 hover:text-gray-900');

export const MobileNav: React.FC = () => {
  const location = useLocation();
  const chatUnread = useChatStore((s) => s.totalUnread);
  const inboxUnread = useInboxStore((s) => s.unread);

  const isActive = (path: string): boolean =>
    location.pathname === path || location.pathname.startsWith(`${path}/`);

  const items: { icon: React.ElementType; label: string; path: string; unread: number }[] = [
    { icon: Briefcase, label: 'Business', path: '/business', unread: 0 },
    { icon: MessageCircle, label: 'Workspace', path: '/chat', unread: chatUnread },
    { icon: Workflow, label: 'Automate', path: '/studio', unread: 0 },
    { icon: InboxIcon, label: 'Inbox', path: '/inbox', unread: inboxUnread },
    { icon: User, label: 'Account', path: '/account', unread: 0 },
  ];

  return (
    <nav
      aria-label="Mobile navigation"
      className="lg:hidden fixed bottom-0 inset-x-0 z-40 flex items-stretch justify-around bg-white border-t border-gray-200 pb-[env(safe-area-inset-bottom)] shadow-[0_-2px_10px_rgba(0,0,0,0.05)]"
    >
      {items.map((item) => {
        const active = isActive(item.path);
        const Icon = item.icon;
        return (
          <Link
            key={item.path}
            to={item.path}
            onClick={() => { void hapticTap(); }}
            onMouseEnter={() => prefetchRoute(item.path)}
            onFocus={() => prefetchRoute(item.path)}
            aria-label={item.unread > 0 ? `${item.label}, ${item.unread} unread` : item.label}
            aria-current={active ? 'page' : undefined}
            className={itemClass(active)}
          >
            <span className="relative">
              <Icon className="w-5 h-5" aria-hidden="true" />
              {item.unread > 0 && (
                <span className="absolute -top-1.5 -right-2 min-w-[15px] h-[15px] px-0.5 rounded-full bg-gray-900 text-white text-[9px] leading-[15px] text-center font-semibold">
                  {item.unread > 9 ? '9+' : item.unread}
                </span>
              )}
            </span>
            <span>{item.label}</span>
          </Link>
        );
      })}
    </nav>
  );
};

export default MobileNav;
